import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';

/**
 * ★★★ WT lote 1 — O REGISTRO DE ERRO DO CLIENTE.
 *
 * ⚠⚠ O buraco que isto fecha, medido em `docs/wt-medicao.md` §9: **nenhum erro do navegador
 * saía do navegador**. Zero `ErrorBoundary`, zero `window.onerror`, zero telemetria — e
 * **87 `console.error` em 50 arquivos** morrendo na máquina de quem usou.
 *
 * ★ O custo disso já foi medido duas vezes: o caso da Jéssica (03/out) só apareceu quando
 * **ela reclamou**, e o defeito da peça WC sobreviveu **sete semanas** porque a recusa de
 * permissão virava ausência silenciosa.
 *
 * ★★ POR QUE NO FIRESTORE, e não num serviço de fora: o dado de erro de uma clínica de saúde
 * passa pela seção 9 do PLANO (LGPD). ⚠ Mandar para terceiro exigiria contrato e
 * anonimização provada; aqui o dado fica no mesmo banco, sob as mesmas rules, e **só o
 * superadmin lê**.
 */

/** As chaves gravadas. ⚠ LISTA FECHADA — as rules exigem exatamente estas. */
export const CAMPOS_DO_ERRO = [
  'criadoEm', 'uid', 'papel', 'clinicaId', 'tela', 'origem', 'codigo', 'mensagem', 'bundle', 'navegador',
  // ★★★ WT lote 1b — veio do PRIMEIRO registro real, não do desenho.
  'tipo',
  /**
   * ★★★ WU-2/P1 — O **ASSUNTO**, e ele nasceu de uma medição que não pôde ser fechada.
   *
   * ⚠⚠⚠ Até aqui `tratarErro` jogava o assunto fora quando o erro trazia `code`
   * (`codigo: erro?.code ?? `falha:${nome}``). ★ Medido na WU
   * (`docs/wu-reincidencia-1610.md` §5.2): o registro de **16/out 13:34:39Z** diz
   * `unavailable` em `/proximos-contatos` — e essa tela tem **cinco** listeners (leads,
   * equipe, metas, próximos contatos, configuração da clínica). ⚠⚠ **Não houve como saber
   * qual falhou**, e por isso a hipótese 4 daquela medição ficou aberta.
   *
   * ★★ `codigo` continua sendo o código do erro, porque é por ele que se agrupa o problema;
   * o assunto é **o que não carregou**. ⚠ Os dois num campo só obrigariam a tela de Saúde a
   * desfazer a cola — e um campo que precisa ser desfeito volta a esconder o assunto.
   *
   * ⚠⚠ A lista continua EXATA e as rules pedem a mesma chave: cliente novo com rules velhas
   * seria recusado em TODO registro. ★ Por isso o deploy é **rules primeiro, hosting
   * depois** (ver `docs/wu2-testes.md`).
   */
  'assunto',
];

export const TIPOS = ['uso', 'sistema'];

/**
 * ★★★ WT lote 1b — ERRO DE USO × ERRO DE SISTEMA.
 *
 * ⚠⚠ Nasceu do primeiro registro em produção (06/out 07:32): `functions/invalid-argument`,
 * *"Informe um celular com DDD"* — a Jéssica digitou um telefone sem DDD e o servidor
 * **recusou corretamente**. ★ **Não havia nada a consertar**, e com uma clínica isso é
 * curiosidade. ⚠ Com dez clínicas, o `invalid-argument` de digitação **soterra** o
 * `permission-denied` que importa, e a tela de Saúde perde o motivo de existir.
 *
 * ★★ A REGRA, estreita de propósito: é `'uso'` **só** quando vem de `callable` **e** o
 * código é de validação de entrada. ⚠ Tudo o mais é `'sistema'` — inclusive o que não se
 * sabe classificar. ★ Errar para o lado de `'sistema'` é o lado certo: um erro de uso
 * tratado como sistema **aparece e se explica**; um erro de sistema escondido na aba de uso
 * **não aparece nunca**.
 *
 * ⚠ `permission-denied` **não** é uso, mesmo vindo de callable: a pessoa não "usou errado" —
 * ou a regra está apertada demais, ou a tela ofereceu algo que o papel não pode. As duas
 * são defeito.
 *
 * ⚠⚠ `resource-exhausted` (o teto anti-ban do WhatsApp) também **não** é uso: é o sistema
 * protegendo o número, e o dono precisa saber que aconteceu.
 */
const CODIGOS_DE_ENTRADA = ['invalid-argument', 'failed-precondition'];

export function classificarErro({ origem, codigo } = {}) {
  if (origem !== 'callable') return 'sistema';
  // ★ o `code` das callables chega prefixado (`functions/invalid-argument`)
  const curto = String(codigo ?? '').replace(/^functions\//, '');
  return CODIGOS_DE_ENTRADA.includes(curto) ? 'uso' : 'sistema';
}

export const ORIGENS = ['render', 'global', 'listener', 'service', 'callable'];

/** Teto por sessão por hora, e janela do dedup. */
export const TETO_POR_HORA = 20;
export const JANELA_DEDUP_MS = 60_000;
export const LIMITE_MENSAGEM = 300;

/**
 * ★★★ A LIMPEZA É LGPD, NÃO ENFEITE.
 *
 * ⚠⚠ Mensagem de erro do Firestore e da Evolution **carrega dado do documento que falhou**.
 * Exemplos reais: *"Missing or insufficient permissions"* é inócua, mas
 * *"Evolution API recusou o envio para 5562998765432"* e
 * *"usuario joao@clinica.com.br sem permissao"* não são.
 *
 * ★ Então: corrida de **8 ou mais dígitos** sai (é telefone, CPF, id longo), e-mail sai, e o
 * resto é cortado em 300. ⚠ 8 e não 11: o JID chega sem o nono dígito e às vezes sem DDD.
 *
 * ★★ O que NÃO é removido, de propósito: números curtos (códigos HTTP, contagens, linhas) —
 * eles são o diagnóstico, e 3 dígitos não identificam ninguém.
 */
export function limparMensagem(texto) {
  const cru = typeof texto === 'string' ? texto : String(texto?.message ?? texto ?? '');
  return cru
    // e-mail primeiro: ele pode conter dígitos que o padrão de telefone comeria pela metade
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]')
    /**
     * ⚠⚠ A CORRIDA DE NÚMERO: começa e TERMINA em dígito.
     *
     * ★ A primeira versão era `(?:\d[\s().-]?){8,}` e **comia o separador seguinte**:
     * `"envio para 5562998765432 falhou"` virava `"envio para [numero]falhou"`, colando as
     * palavras. Quem denunciou foi o teste — não o olho.
     *
     * ★ Até DOIS separadores entre dígitos, para pegar `+55 (62) 99664-0861` como UMA
     * corrida; sem isso o `") "` partia o telefone em pedaços curtos e nenhum pedaço
     * atingia o mínimo.
     *
     * ⚠ Efeito colateral aceito: uma data como `2026-10-05` tem 8 dígitos e **é mascarada**.
     * ★ Perda pequena — o `criadoEm` do registro já diz quando foi — e errar para o lado de
     * esconder é o lado certo aqui.
     */
    .replace(/\d(?:[\s()+.-]{0,2}\d){7,}/g, '[numero]')
    .trim()
    .slice(0, LIMITE_MENSAGEM);
}

/**
 * ★ O bundle, lido da própria página. Não há `define` no build nem variável de ambiente:
 * o nome do arquivo servido JÁ é o hash, e é o que o dono usa para saber se a pessoa
 * recarregou. ⚠ Em dev e em teste não existe — fica `null`, e isso é informação também.
 */
function bundleAtual() {
  try {
    const src = document.querySelector('script[type="module"][src*="/assets/index-"]')?.getAttribute('src') ?? '';
    return src.match(/index-([A-Za-z0-9_-]+)\.js/)?.[1] ?? null;
  } catch { return null; }
}

/**
 * ★ Só NOME e VERSÃO do navegador. ⚠ O `userAgent` inteiro é identificador de máquina
 * (modelo, build do sistema): guardá-lo transformaria o registro de erro num rastreador.
 */
function navegadorAtual() {
  try {
    const ua = navigator.userAgent ?? '';
    const m = ua.match(/(Edg|OPR|Chrome|Firefox|Safari)\/(\d+)/);
    if (!m) return null;
    const nome = { Edg: 'Edge', OPR: 'Opera' }[m[1]] ?? m[1];
    return `${nome} ${m[2]}`;
  } catch { return null; }
}

/** A rota atual, sem query nem hash — ⚠ a query pode levar id de lead. */
function telaAtual() {
  try { return window.location?.pathname ?? null; } catch { return null; }
}

/**
 * ★ Quem é a pessoa. Fica em módulo, não em contexto do React: `window.onerror` e
 * `unhandledrejection` disparam FORA da árvore, e eram justamente os que não tinham dono.
 */
let contexto = { clinicaId: null, uid: null, papel: null };
export function definirContextoDeErro({ clinicaId = null, uid = null, papel = null } = {}) {
  contexto = { clinicaId, uid, papel };
}
export const contextoDeErro = () => ({ ...contexto });

/** Estado das proteções. Exportado só para o teste poder zerar entre casos. */
const vistos = new Map();
let janelaHora = 0;
let gravadosNaHora = 0;
export function zerarProtecoesDeErro() {
  vistos.clear();
  janelaHora = 0;
  gravadosNaHora = 0;
}

/**
 * ★★★ REGISTRA UM ERRO. **Nunca lança** — nem quando a gravação falha.
 *
 * ⚠⚠ Um registro de erro que estoura é a pior troca possível: ele transformaria um defeito
 * de uma tela numa quebra do app inteiro, e apareceria **dentro** do `ErrorBoundary` que o
 * chamou. ★ Daí o `try` em volta de tudo e o `.catch` na escrita.
 *
 * ★ Devolve `true` se gravou, `false` se não — é o que os testes leem.
 */
export async function registrarErro({ origem, tela, codigo, mensagem, assunto, extra } = {}) {
  try {
    const { clinicaId, uid, papel } = contexto;
    const doc = {
      origem: ORIGENS.includes(origem) ? origem : 'service',
      tela: tela ?? telaAtual(),
      codigo: String(codigo ?? '').slice(0, 60) || 'sem-codigo',
      mensagem: limparMensagem(mensagem),
      /**
       * ★★★ WU-2/P1 — o assunto é gravado SEMPRE, inclusive vazio.
       *
       * ⚠ `''` é informação: diz "este erro não tem assunto" (render, `window.onerror`) — e é
       * diferente de *"tinha assunto e foi descartado"*, que era o estado anterior. ★ A tela de
       * Saúde mostra `—` no vazio.
       */
      assunto: String(assunto ?? '').slice(0, 60),
    };
    // ★ WT 1b: classificado na GRAVAÇÃO, não na leitura — a tela não pode ser uma segunda
    // definição do que é erro de uso, e registro antigo sem `tipo` conta como 'sistema'.
    doc.tipo = classificarErro(doc);

    /**
     * ★★ SEM LOGIN, SÓ CONSOLE — e não é limitação, é a regra do caminho.
     *
     * ⚠ O documento vive em `clinicas/{clinicaId}/erros`: sem clínica não há caminho, e sem
     * `uid` as rules recusariam (elas exigem `uid == request.auth.uid`). ★ Erro na tela de
     * login continua indo para o console, como antes.
     */
    if (!clinicaId || !uid) {
      console.error('[OdontoSDR] erro sem sessão (não registrado):', doc.origem, doc.codigo, doc.mensagem);
      return false;
    }

    // ★ dedup: a mesma falha repetida em 60 s é UM problema, não cinquenta. ⚠ Um listener
    // que falha costuma falhar a cada reconexão — sem isto, uma tela aberta encheria a
    // coleção sozinha.
    /**
     * ⚠⚠ WU-2/P1 — o ASSUNTO entra na chave do dedup, e isso **conserta uma perda**: dois
     * listeners da mesma tela que falham com o mesmo `code` em 60 s eram **um** registro, e o
     * que sobrevivia não dizia qual. ★ Agora são dois registros, cada um com o seu assunto.
     * ★★★ A chave do agrupamento da Saúde (`produtividade/saude.js`) acompanha — as duas
     * **têm** de ser a mesma, senão a contagem da tela mente.
     */
    const chave = `${doc.origem}|${doc.codigo}|${doc.assunto}|${doc.tela}`;
    const agora = Date.now();
    const quando = vistos.get(chave);
    if (quando !== undefined && agora - quando < JANELA_DEDUP_MS) return false;

    // ★ teto por hora: trava de custo. ⚠ Um laço de render com erro geraria milhares de
    // escritas em segundos, e escrita é dinheiro.
    const hora = Math.floor(agora / 3_600_000);
    if (hora !== janelaHora) { janelaHora = hora; gravadosNaHora = 0; }
    if (gravadosNaHora >= TETO_POR_HORA) {
      console.error('[OdontoSDR] teto de registros desta hora atingido; só console:', doc.codigo, doc.mensagem);
      return false;
    }

    vistos.set(chave, agora);
    gravadosNaHora += 1;

    /**
     * ⚠⚠ `extra` NÃO É GRAVADO. Ele existe na assinatura porque quem chama quer passar
     * contexto — e é exatamente por isso que ele não pode ir para o banco: o contexto mais
     * à mão é o documento que falhou (lead, conversa, mensagem), com nome e telefone de
     * paciente dentro. ★ Vai para o console, que é local.
     */
    if (extra !== undefined) console.error('[OdontoSDR] contexto do erro (só local):', extra);

    await addDoc(collection(db, 'clinicas', clinicaId, 'erros'), {
      ...doc,
      uid,
      papel: papel ?? null,
      clinicaId,
      bundle: bundleAtual(),
      navegador: navegadorAtual(),
      criadoEm: serverTimestamp(),
    });
    return true;
  } catch (e) {
    console.error('[OdontoSDR] falha ao registrar o erro (ignorada):', e);
    return false;
  }
}

/**
 * ★★★ O PONTO ÚNICO dos serviços.
 *
 * Devolve o tratador que vai para o `onSnapshot`/`catch`: ele **registra** e **repassa**.
 *
 * ★★ POR QUE EMBRULHAR DENTRO DO SERVIÇO, e não só nas telas: a medição achou **11**
 * tratadores `() => {}` e **22** que só logavam. ⚠ Se o registro dependesse de quem chama,
 * o décimo segundo `() => {}` voltaria a não registrar nada — e ninguém notaria. ★ Aqui o
 * chamador pode até não passar nada: o erro é registrado de todo jeito.
 *
 * ⚠ `nome` é o ASSUNTO ("equipe", "metas"), não a mensagem: ele vira o `codigo` quando o
 * erro não traz um.
 */
export function tratarErro(nome, aoFalhar, origem = 'listener') {
  return (erro) => {
    registrarErro({
      origem,
      codigo: erro?.code ?? `falha:${nome}`,
      // ★★★ WU-2/P1: o assunto vai em campo PRÓPRIO — antes ele só aparecia quando o erro
      // **não** tinha `code`, que é justamente o caso menos frequente.
      assunto: nome,
      mensagem: erro?.message ?? String(erro ?? ''),
    });
    console.error(`[OdontoSDR] Falha em ${nome}:`, erro);
    try {
      aoFalhar?.(erro);
    } catch (e) {
      // ⚠ O tratador da TELA estourando não pode virar erro não tratado aqui.
      console.error('[OdontoSDR] tratador da tela estourou:', e);
    }
  };
}

/** ★ Mesmo ponto único, para chamada de callable: o `code` do erro é o que importa. */
export function tratarErroDeCallable(nome, erro) {
  registrarErro({
    origem: 'callable',
    codigo: erro?.code ?? `falha:${nome}`,
    // ★ Aqui o assunto é o NOME DA CALLABLE — e com ele o `permission-denied` de callable
    // deixa de ser anônimo entre as 22 funções.
    assunto: nome,
    mensagem: erro?.message ?? String(erro ?? ''),
  });
  return erro;
}
